import clsx from 'clsx'
import { useRouter } from 'next/router'
import type { ComponentType } from 'react'
import React, { useEffect } from 'react'

import { Spinner } from '@wartek-id/spinner'

import { useFeatureFlagContext } from 'utils/hooks/context/useFeatureFlagContext'
import { usePPGDaljabContext } from 'utils/hooks/context/usePPGDaljabContext'

import Confirmation from 'components/Confirmation'
import ErrorBlock from 'components/ErrorBlock'
import { Header as BaseHeader } from 'components/Header'
import MaintenanceInfo from 'components/MaintenanceInfo'
import NotEligible from 'components/NotEligible'

export interface LayoutProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
  hasHeader?: boolean
  headerHasBackButton?: boolean
  headerTitle?: string
  backHref?: string
  header?: ComponentType
}

export const Layout = ({
  children,
  hasHeader = false,
  headerTitle = '',
  headerHasBackButton = false,
  className,
  header: PropHeader,
}: LayoutProps) => {
  const Header =
    PropHeader ??
    (() =>
      BaseHeader({ title: headerTitle, hasBackButton: headerHasBackButton }))

  const router = useRouter()

  const { getFeatureByKey, status } = useFeatureFlagContext()
  const {
    isLoading: isCheckingEligibleLoading,
    data: profileData,
    isEligible,
    isError,
    isConfirmed,
    setConfirmed,
  } = usePPGDaljabContext()
  const isOnMaintenanceLoading = status === 'loading'
  const isOnMaintenance = getFeatureByKey('enableMaintenancePpgDaljab')
  const isEnableNonEligible = getFeatureByKey('enablePpgDaljabNotEligible')

  useEffect(() => {
    if (
      !isError &&
      !isEligible &&
      !isEnableNonEligible &&
      !isOnMaintenanceLoading &&
      !isCheckingEligibleLoading
    ) {
      router.replace('/404')
    }
  }, [
    isEnableNonEligible,
    isOnMaintenanceLoading,
    isCheckingEligibleLoading,
    isEligible,
    isError,
    router,
  ])

  const Content = () => {
    if (isOnMaintenanceLoading || isCheckingEligibleLoading) {
      return (
        <div className="h-full flex items-center justify-center">
          <Spinner />
        </div>
      )
    }

    if (isOnMaintenance) {
      return <MaintenanceInfo />
    }

    if (isError) {
      return (
        <>
          <ErrorBlock
            title="Terjadi Kesalahan"
            description=" Mohon maaf, terjadi kesalahan pada sistem. Silahkan coba beberapa saat lagi."
            click={() => window.location.reload()}
            actionWord="Muat ulang"
          />
        </>
      )
    }

    if (!isEligible) {
      return <NotEligible />
    }

    if (!isConfirmed) {
      return (
        <Confirmation data={profileData!} onConfirm={() => setConfirmed()} />
      )
    }

    return <div>{children}</div>
  }

  return (
    <div
      className={clsx(
        'max-w-xl m-auto relative',
        hasHeader && 'mt-14',
        className
      )}
      style={{
        height: 'calc(100vh - 3.5rem)',
      }}
    >
      {hasHeader && <Header />}

      <Content />
    </div>
  )
}

export default Layout
